/**
 * Process Handlers - Main Export
 *
 * Centralized export point for all process handlers organized by domain
 *
 * Architecture:
 * - config.ts: Configuration and utilities
 * - typeMappers.ts: Type conversion between backend and frontend
 * - users.ts: User-related handlers
 * - sessions.ts: Session-related handlers
 * - teams.ts: Team-related handlers
 * - sprints.ts: Sprint and training sprint handlers
 * - facilitatorDashboard.ts: Facilitator dashboard data aggregation
 * - gamer.ts: Gamer-specific handlers (activities, dashboard)
 * - reports.ts: Report handlers
 * - groups.ts: Group handlers
 * - search.ts: Search handlers
 * - questionnaires.ts: Questionnaire handlers
 * - configHandlers.ts: App and user configuration
 * - whereby.ts: Whereby meeting integration
 */

// Re-export configuration and utilities
export { USE_MOCKS, handleRequest } from './config'

// Re-export type mappers
export * from './typeMappers'

// Re-export all domain handlers
export * from './users'
export * from './sessions'
export * from './teams'
export * from './sprints'
export * from './facilitatorDashboard'
export * from './facilitatorLiveSession'
export * from './gamer'
export * from './reports'
export * from './groups'
export * from './search'
export * from './questionnaires'
export * from './configHandlers'
export * from './whereby'
export * from './wherebyRerun'
export * from './videos'
export * from './blogPosts'
export * from './quotes'
export * from './landingConfig'
export * from './feedbackData'
export * from './achievements'
export * from './reportReadiness'
export * from './aiCoach'
export * from './appAssistant'
export * from './coach'
export * from './leader'
export * from './translations'
export * from './sprintWizardConfig'
export * from './inAppNotifications'
export * from './reflections'
export * from './organizations'

// Legacy export: processHandlers object for backwards compatibility
// This allows existing code to continue using processHandlers.functionName()
import * as users from './users'
import * as sessions from './sessions'
import * as teams from './teams'
import * as sprints from './sprints'
import * as dashboard from './facilitatorDashboard'
import * as facilitatorLiveSession from './facilitatorLiveSession'
import * as gamer from './gamer'
import * as reports from './reports'
import * as groups from './groups'
import * as search from './search'
import * as questionnaires from './questionnaires'
import * as configHandlers from './configHandlers'
import * as whereby from './whereby'
import * as wherebyRerun from './wherebyRerun'
import * as feedbackData from './feedbackData'
import * as achievements from './achievements'
import * as reportReadiness from './reportReadiness'
import * as aiCoach from './aiCoach'
import * as appAssistant from './appAssistant'
import * as translations from './translations'
import * as videos from './videos'
import * as blogPosts from './blogPosts'
import * as quotes from './quotes'
import * as landingConfig from './landingConfig'
import * as sprintWizardConfig from './sprintWizardConfig'
import * as inAppNotifications from './inAppNotifications'
import * as reflections from './reflections'
import * as organizations from './organizations'
import * as coach from './coach'
import * as leader from './leader'

/**
 * Legacy processHandlers object
 * Example migration:
 * ```typescript
 * // OLD
 * import { processHandlers } from './apiLayer'
 * processHandlers.getUsers()
 *
 * // NEW
 * import { getUsers } from './apiLayer'
 * getUsers()
 * ```
 */
export const processHandlers = {
  // Users
  ...users,

  // Sessions
  ...sessions,

  // Teams
  ...teams,

  // Sprints
  ...sprints,

  // Dashboard
  ...dashboard,
  ...facilitatorLiveSession,

  // Gamer
  ...gamer,

  // Reports
  ...reports,

  // Groups
  ...groups,

  // Search
  ...search,

  // Questionnaires
  ...questionnaires,

  // Configuration
  ...configHandlers,

  // Whereby
  ...whereby,

  // Whereby (admin/dev)
  ...wherebyRerun,

  // Feedback
  ...feedbackData,

  // Achievements
  ...achievements,

  // Report Readiness
  ...reportReadiness,

  // AI Coach
  ...aiCoach,

  // App Assistant
  ...appAssistant,

  // Translations
  ...translations,

  // Videos
  ...videos,

  // Blog Posts
  ...blogPosts,

  // Quotes
  ...quotes,

  // Landing config
  ...landingConfig,

  // Sprint Wizard Config
  ...sprintWizardConfig,

  // In-App Notifications
  ...inAppNotifications,

  // Reflections
  ...reflections,

  // Organizations (multi-tenant)
  ...organizations,

  // One-on-One Coach (Aria)
  ...coach,

  // Leader (My people)
  ...leader
}
